import { useMutation, useQuery } from '@tanstack/react-query'
import AXIOS_INSTANCE from '../api/axiosInstance'
import { queryClient } from '../store/provider/TanStackProvider'
import {
  ConsentFormDataMock,
  ConsentFormResMock,
  IntakeConsentData,
  IntakeConsetMock
} from '../types/consent_form.type'

// Props for fetching consent forms
type Props = {
  id?: string
  page?: number
  limit?: number
  query?: string
  patient_id?: string
  encounter_id?: string
  sortDir?: 'asc' | 'desc'
  category?: string
  order_state?: string
  intake_id?: string
  document_template_id?: string
  status?: string
}

export const useConsentForms = (arg?: Props) => {
  const id = arg?.id
  const query = arg?.query
  const limit = arg?.limit || 10
  const page = arg?.page || 1
  const patient_id = arg?.patient_id
  const category = arg?.category
  const order_state = arg?.order_state
  const sortDir = arg?.sortDir || 'desc'
  const encounter_id = arg?.encounter_id
  const intake_id = arg?.intake_id
  const document_template_id = arg?.document_template_id
  const status = arg?.status

  // Fetch all consent forms (with pagination and query)
  const { data: consentForms, isLoading: consentFormsIsLoading } = useQuery({
    queryFn: async () => {
      let endpoint = 'consent_forms?'
      if (page) endpoint += `page%5Bnumber%5D=${page}&page%5Bsize%5D=${limit}&`
      if (status) endpoint += `status=${status}&`
      if (query) endpoint += `query=${query}&`
      if (patient_id) endpoint += `patient_id=${patient_id}&`
      if (encounter_id) endpoint += `encounter_id=${encounter_id}&`
      if (category) endpoint += `category=${category}&`
      if (order_state) endpoint += `order_state=${order_state}&`
      if (sortDir) endpoint = `${endpoint}&sort_dir=${sortDir}&`
      const { data } = await AXIOS_INSTANCE.get<ConsentFormResMock<ConsentFormDataMock[]>>(endpoint)
      return data
    },
    queryKey: ['consent_forms', { category, encounter_id, id, order_state, page, patient_id, query, sortDir, status }]
  })

  const { data: singleConsentForm, isLoading: singleConsentFormIsLoading } = useQuery<
    ConsentFormResMock<ConsentFormDataMock>
  >({
    enabled: !!id,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get(`consent_forms/${id}`)
      return data
    },
    queryKey: ['consent_forms', id]
  })

  const { data: previewConsentForm, isLoading: previewConsentFormIsLoading } = useQuery({
    enabled: !!document_template_id,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<{ data: IntakeConsentData }>(`consent_forms/preview`, {
        params: {
          ...(document_template_id && { document_template_id }),
          ...(patient_id && { patient_id })
        }
      })
      return data.data
    },
    queryKey: ['consent_forms', 'intake_consent', document_template_id]
  })

  const { mutateAsync: uploadConsent } = useMutation({
    mutationFn: async (formVal: any) => {
      const { data } = await AXIOS_INSTANCE.post('consent_forms/upload', formVal, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['intake_consent'] })
      queryClient.invalidateQueries({ queryKey: ['consent_forms'] })
      queryClient.invalidateQueries({ queryKey: ['intake'] })
    }
  })

  const { mutateAsync: createConsent } = useMutation({
    mutationFn: async (formVal: any) => {
      const { data } = await AXIOS_INSTANCE.post('consent_forms', formVal, {
        headers: {
          'Content-Type': 'application/vnd.api+json'
        }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['intake_consent'] })
      queryClient.invalidateQueries({ queryKey: ['consent_forms'] })
    }
  })

  const { mutateAsync: addConsent } = useMutation({
    mutationFn: async (formVal: any) => {
      const { data } = await AXIOS_INSTANCE.post('consent_forms/add', formVal)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['intake_consent'] })
      queryClient.invalidateQueries({ queryKey: ['consent_forms'] })
      queryClient.invalidateQueries({ queryKey: ['intake'] })
      queryClient.invalidateQueries({ queryKey: ['template_doc'] })
    }
  })

  const { data: intakeConsent } = useQuery({
    enabled: !!intake_id,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<IntakeConsetMock>('consent_forms/intake_required_consents', {
        params: {
          ...(intake_id && { intake_id }),
          ...(patient_id && { patient_id })
        }
      })
      return data.data
    },
    queryKey: ['intake_consent', { intake_id, patient_id }]
  })

  // Update a consent form
  const { mutateAsync: updateConsentForm } = useMutation({
    mutationFn: async ({ formVal, id }: { formVal: any; id: string }) => {
      const { data } = await AXIOS_INSTANCE.patch(`consent_forms/${id}`, formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consent_forms'] })
      queryClient.invalidateQueries({ queryKey: ['intake_consent'] })
      queryClient.invalidateQueries({ queryKey: ['intake'] })
    }
  })

  return {
    addConsent,
    consentFormPagination: consentForms?.meta.pagination,
    consentForms: consentForms?.data,
    consentFormsIsLoading,
    createConsent,
    included: consentForms?.included,
    intakeConsent,
    previewConsentForm,
    previewConsentFormIsLoading,
    singleConsentForm,
    singleConsentFormData: singleConsentForm?.data,
    singleConsentFormIsLoading,
    updateConsentForm,
    uploadConsent
  }
}
